/**
 * FileSelectionButton component module.
 * @module shared/components/FileSelectionButton
 *
 */

import React, {useRef, useState} from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import {_ButtonBase, secondaryImportanceButton} from './cssUtils';
import Row from './Row';

const getColor = props => {
  if (props.disabled) {
    return props.theme.dark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)';
  }
  return props.theme.dark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)';
};

const _Button = styled.div`
  height: ${props => props.height};
  width: auto;
  flex-grow: 0;
  
  ${_ButtonBase};
  ${props => ({...(secondaryImportanceButton(props))})};
  opacity: ${props => props.disabled ? 0.5 : 1};
  cursor: ${props => !props.disabled ? 'pointer' : 'not-allowed'};
`;

const _Label = styled.label`
  cursor: ${props => !props.disabled ? 'pointer' : 'not-allowed'};
  margin: 0 5px 0 5px;
`;

const _FileLabel = styled.div`
  color: ${(props) => getColor(props)};
  display: flex;
  flex-grow: 1;
  font-family: ${(props) => props.theme.bodyText.name};
  font-size: ${(props) => props.theme.bodyText.size};
  margin: 5px;
  overflow-wrap: break-word;
  text-align: left;
  width: 100px;
`;

const _InputFile = styled.input`
  display: none !important;
`;

/**
 * File Selection Button component
 * @name FileSelectionButton
 */
const FileSelectionButton = (props) => {
  const {allowedTypes, height = 'auto', isMultiFile, label, onChange, width, disabled = false} = props;
  const [selectedFiles, setSelectedFiles] = useState({});
  const inputFile = useRef(null);

  const attributes = {
    accept: allowedTypes.toString(),
    disabled: false,
    id: 'fileUpload',
    name: 'fileUpload',
    type: 'file',
  };

  if (isMultiFile) {
    attributes.multiple = true;
  }

  const onButtonClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (inputFile && inputFile.current) {
      inputFile.current.click();
    }
  };

  const onFileSelection = (target) => {
    if (!target.files.length) return;

    setSelectedFiles(target.files);
  };

  const fileNames = Object.values(selectedFiles).map(file => file.name);

  return <Row alignSubRow='center' justifySubRow='left' height={height} margin='5px' width={width} wrap={false}>
    <_Button onClick={!disabled ? onButtonClick : undefined}
             type='button'
             disabled={disabled}>
      <_Label disabled={disabled}>{label}</_Label>
    </_Button>
    <_InputFile {...attributes} ref={inputFile} onChange={(e) => {
      onFileSelection(e.target);
      if (onChange) return onChange(e);
    }}/>
    <_FileLabel name='file' disabled={!fileNames.length}>{fileNames.join(', ') || 'No File Selected'}</_FileLabel>
  </Row>;
};

export default FileSelectionButton;

FileSelectionButton.propTypes = {
  /**
   * The accepted file types (e.g. '.pdf' or '.txt')
   */
  allowedTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  /**
   * Whether the button is disabled
   */
  disabled: PropTypes.bool,
  /**
   * The height of the component
   */
  height: PropTypes.string,
  /**
   * If true, then component allows multiple file selections. Otherwise, it's single file.
   */
  isMultiFile: PropTypes.bool,
  /**
   * The button label
   */
  label: PropTypes.string.isRequired,
  /**
   * The on change function for parent to access the selected files
   */
  onChange: PropTypes.func,
  /**
   * The width of the component
   */
  width: PropTypes.string,
};
